/**
 * Mermaid block extension: block-level node that shows a Mermaid diagram.
 * When selected, the user can edit the source code; when not selected, the diagram is rendered.
 * Mirrors the mathematics-inline-edit pattern (block math).
 */

import { Node, mergeAttributes } from '@tiptap/core';
import { NodeSelection, type Transaction } from '@tiptap/pm/state';
import type { Node as PMNode } from '@tiptap/pm/model';
import mermaid from 'mermaid';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    insertMermaid: {
      /**
       * Inserts a Mermaid diagram block with code string.
       * @param options - Options for inserting mermaid diagram.
       * @returns ReturnType
       */
      insertMermaid: (options: { code: string; pos?: number }) => ReturnType;

      /**
       * Updates a Mermaid diagram block with new code.
       * @param options - Options for updating mermaid diagram.
       * @returns ReturnType
       */
      updateMermaid: (options: { code: string; pos?: number }) => ReturnType;

      /**
       * Deletes a Mermaid diagram block.
       * @param options - Options for deleting mermaid diagram.
       * @returns ReturnType
       */
      deleteMermaid: (options?: { pos?: number }) => ReturnType;
    };
  }
}

let mermaidIdCounter = 0;

function createMermaidNodeView(
  editor: {
    view: { dispatch: (tr: unknown) => void; dom: HTMLElement };
    state: { selection: unknown; tr: unknown; doc: unknown };
    commands: { updateMermaid: (opts: { code: string; pos: number }) => boolean };
  },
  getPos: () => number | undefined,
  nodeName: string,
) {
  return ({ node }: { node: PMNode }) => {
    const wrapper = document.createElement('div');
    wrapper.className = 'tiptap-mermaid-render tiptap-mermaid-render--editable';
    wrapper.dataset.type = 'mermaid';
    wrapper.setAttribute('contenteditable', 'false');

    const renderEl = document.createElement('div');
    renderEl.className = 'tiptap-mermaid-preview';
    const inputEl = document.createElement('textarea');
    inputEl.className = 'tiptap-mermaid-input';
    inputEl.rows = 8;
    inputEl.spellcheck = false;
    inputEl.setAttribute('data-mermaid-input', 'block');

    // Create info button; syntax guide opens in a modal overlay (like Workspace Information)
    const inputContainer = document.createElement('div');
    inputContainer.className = 'tiptap-mermaid-input-container';
    inputContainer.style.position = 'relative';
    
    const infoButton = document.createElement('button');
    infoButton.className = 'tiptap-mermaid-info-button';
    infoButton.type = 'button';
    infoButton.setAttribute('aria-label', 'Mermaid syntax help');
    infoButton.setAttribute('title', 'Mermaid syntax help');
    infoButton.innerHTML = `
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    `;
    
    let syntaxModalRoot: HTMLDivElement | null = null;
    
    const closeSyntaxModal = () => {
      if (!syntaxModalRoot || !syntaxModalRoot.parentNode) return;
      document.body.removeChild(syntaxModalRoot);
      document.body.style.overflow = '';
      syntaxModalRoot = null;
    };
    
    const openSyntaxModal = () => {
      if (syntaxModalRoot) return;
      const backdrop = document.createElement('div');
      backdrop.className = 'tiptap-mermaid-syntax-modal-backdrop';
      backdrop.setAttribute('role', 'presentation');
      const dialog = document.createElement('div');
      dialog.className = 'tiptap-mermaid-syntax-modal-dialog';
      dialog.setAttribute('role', 'dialog');
      dialog.setAttribute('aria-modal', 'true');
      dialog.setAttribute('aria-labelledby', 'tiptap-mermaid-syntax-modal-title');
      dialog.innerHTML = `
        <div class="tiptap-mermaid-syntax-modal-header">
          <h2 id="tiptap-mermaid-syntax-modal-title" class="tiptap-mermaid-syntax-modal-title">Mermaid Syntax Guide</h2>
          <button type="button" class="tiptap-mermaid-syntax-modal-close" aria-label="Close">
            <svg class="tiptap-mermaid-syntax-modal-close-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </div>
        <div class="tiptap-mermaid-syntax-modal-body">
          <p class="tiptap-mermaid-syntax-modal-intro">Diagrams start with a declaration of the diagram type, then the definitions. Line comments: <code>%%</code> ignores the rest of the line.</p>
          <section class="tiptap-mermaid-syntax-section">
            <h3 class="tiptap-mermaid-syntax-section-title">Flowchart</h3>
            <p class="tiptap-mermaid-syntax-desc">Good for processes, decisions, and step-by-step flows. Use <code>flowchart TD</code> (top-down) or <code>flowchart LR</code> (left-right).</p>
            <pre class="tiptap-mermaid-syntax-code"><code>flowchart TD
  A[Start] --> B{Decision}
  B -->|Yes| C[End]
  B -->|No| D[Loop back]
  D --> A</code></pre>
          </section>
          <section class="tiptap-mermaid-syntax-section">
            <h3 class="tiptap-mermaid-syntax-section-title">Sequence Diagram</h3>
            <p class="tiptap-mermaid-syntax-desc">Good for interactions between actors over time, API calls, and message passing.</p>
            <pre class="tiptap-mermaid-syntax-code"><code>sequenceDiagram
  participant User
  participant Server
  User->>Server: Request
  Server-->>User: Response</code></pre>
          </section>
          <section class="tiptap-mermaid-syntax-section">
            <h3 class="tiptap-mermaid-syntax-section-title">Class Diagram</h3>
            <p class="tiptap-mermaid-syntax-desc">Good for OOP structure and relationships between classes.</p>
            <pre class="tiptap-mermaid-syntax-code"><code>classDiagram
  class Animal {
    +name: string
    +speak()
  }
  Animal <|-- Dog</code></pre>
          </section>
          <section class="tiptap-mermaid-syntax-section">
            <h3 class="tiptap-mermaid-syntax-section-title">State Diagram</h3>
            <p class="tiptap-mermaid-syntax-desc">Good for state machines, lifecycles, and status flows.</p>
            <pre class="tiptap-mermaid-syntax-code"><code>stateDiagram-v2
  [*] --> Idle
  Idle --> Running: start
  Running --> Idle: stop</code></pre>
          </section>
          <section class="tiptap-mermaid-syntax-section">
            <h3 class="tiptap-mermaid-syntax-section-title">Entity Relationship</h3>
            <p class="tiptap-mermaid-syntax-desc">Good for database schemas and entity relationships.</p>
            <pre class="tiptap-mermaid-syntax-code"><code>erDiagram
  USER ||--o{ ORDER : places
  ORDER {
    string id
    date created
  }</code></pre>
          </section>
          <section class="tiptap-mermaid-syntax-section">
            <h3 class="tiptap-mermaid-syntax-section-title">Gantt</h3>
            <p class="tiptap-mermaid-syntax-desc">Good for project timelines and schedules.</p>
            <pre class="tiptap-mermaid-syntax-code"><code>gantt
  title Project Plan
  dateFormat YYYY-MM-DD
  section Phase 1
  Design    :a1, 2024-01-01, 30d
  section Phase 2
  Build     :a2, after a1, 45d</code></pre>
          </section>
          <section class="tiptap-mermaid-syntax-section">
            <h3 class="tiptap-mermaid-syntax-section-title">Pie Chart &amp; More</h3>
            <p class="tiptap-mermaid-syntax-desc">Pie: <code>pie title "Title"</code> then <code>"Label" : value</code>. Also: <code>journey</code> (user journey), <code>gitgraph</code> (branches/commits).</p>
          </section>
          <section class="tiptap-mermaid-syntax-section tiptap-mermaid-syntax-docs-link">
            <a href="https://mermaid.js.org/intro/syntax-reference.html" target="_blank" rel="noopener noreferrer" class="tiptap-mermaid-syntax-link">Mermaid diagram syntax reference →</a>
          </section>
        </div>
      `;
      syntaxModalRoot = document.createElement('div');
      syntaxModalRoot.className = 'tiptap-mermaid-syntax-modal-root';
      syntaxModalRoot.appendChild(backdrop);
      syntaxModalRoot.appendChild(dialog);
      
      const onClose = () => {
        closeSyntaxModal();
        document.removeEventListener('keydown', onEscape);
      };
      const onEscape = (e: KeyboardEvent) => {
        if (e.key === 'Escape') onClose();
      };
      
      backdrop.addEventListener('click', (e) => {
        if (e.target === backdrop) onClose();
      });
      const closeBtn = dialog.querySelector('.tiptap-mermaid-syntax-modal-close');
      if (closeBtn) closeBtn.addEventListener('click', onClose);
      document.addEventListener('keydown', onEscape);
      
      document.body.style.overflow = 'hidden';
      document.body.appendChild(syntaxModalRoot);
    };
    
    infoButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      openSyntaxModal();
    });
    
    inputContainer.appendChild(inputEl);
    inputContainer.appendChild(infoButton);
    
    wrapper.appendChild(renderEl);
    wrapper.appendChild(inputContainer);

    let currentNode = node;
    let renderPromise: Promise<void> | null = null;
    
    // Helper function to check if an element is hidden
    // Only check inline style - if not set to 'none', it's visible (CSS sets display: block)
    const isHidden = (el: HTMLElement): boolean => {
      return el.style.display === 'none';
    };
    
    const isVisible = (el: HTMLElement): boolean => {
      return el.style.display !== 'none';
    };

    async function renderMermaid(code: string) {
      renderEl.textContent = '';
      renderEl.classList.remove('tiptap-mermaid-error');
      if (!code.trim()) {
        renderEl.classList.add('tiptap-mermaid-empty');
        return;
      }
      const id = `mermaid-${++mermaidIdCounter}-${Date.now()}`;
      try {
        mermaid.initialize({
          startOnLoad: false,
          securityLevel: 'strict',
          suppressErrorRendering: true, // Prevent Mermaid from rendering error messages to DOM
        });
        const { svg, bindFunctions } = await mermaid.render(id, code);
        // Check if SVG contains error indicators (Mermaid sometimes renders error SVGs)
        if (
          svg.includes('Syntax error') ||
          svg.includes('Error') ||
          svg.includes('mermaid-error')
        ) {
          throw new Error('Mermaid syntax error');
        }
        renderEl.innerHTML = svg;
        if (bindFunctions) {
          bindFunctions(renderEl);
        }
      } catch (err) {
        // Clear any error content Mermaid might have rendered
        renderEl.innerHTML = '';
        renderEl.classList.add('tiptap-mermaid-error');
        const msg = document.createElement('div');
        msg.className = 'tiptap-mermaid-error-msg';
        msg.textContent = 'Invalid Mermaid';
        const pre = document.createElement('pre');
        pre.className = 'tiptap-mermaid-error-code';
        pre.textContent = code || '(empty)';
        renderEl.appendChild(msg);
        renderEl.appendChild(pre);
      }
    }

    function showRenderMode() {
      const pos = getPos();
      if (typeof pos !== 'number') return;
      const newCode = inputEl.value;
      // Hide input container and input, show render
      inputContainer.style.display = 'none';
      inputEl.style.display = 'none';
      renderEl.style.display = 'block';
      closeSyntaxModal();
      if (newCode !== currentNode.attrs.code) {
        (editor.commands as any).updateMermaid({ code: newCode, pos });
      }
      renderPromise = renderMermaid(newCode);
    }

    function showEditMode() {
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/1182fcbd-95f8-4860-ba2d-64c3e49fe5b9', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          location: 'mermaid-block.ts:113',
          message: 'showEditMode called',
          data: { code: currentNode.attrs.code, codeLength: currentNode.attrs.code?.length || 0 },
          timestamp: Date.now(),
          runId: 'run1',
          hypothesisId: 'C',
        }),
      }).catch(() => {});
      // #endregion
      // Hide render, show input container and input
      renderEl.style.display = 'none';
      inputContainer.style.display = 'block';
      inputEl.style.display = 'block';
      inputEl.value = currentNode.attrs.code ?? '';
      // Force a reflow to ensure display changes take effect
      void inputContainer.offsetHeight;
      inputEl.focus();
      inputEl.select();
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/1182fcbd-95f8-4860-ba2d-64c3e49fe5b9', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          location: 'mermaid-block.ts:119',
          message: 'After showEditMode',
          data: {
            inputDisplay: inputEl.style.display,
            renderDisplay: renderEl.style.display,
            inputValue: inputEl.value,
          },
          timestamp: Date.now(),
          runId: 'run1',
          hypothesisId: 'C',
        }),
      }).catch(() => {});
      // #endregion
    }

    function isThisNodeSelected(): boolean {
      const pos = getPos();
      if (typeof pos !== 'number') return false;
      const sel = editor.state.selection;
      return sel instanceof NodeSelection && sel.from === pos;
    }

    function checkSelection() {
      const pos = getPos();
      if (typeof pos !== 'number') return;
      const isSelected = isThisNodeSelected();
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/1182fcbd-95f8-4860-ba2d-64c3e49fe5b9', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          location: 'mermaid-block.ts:128',
          message: 'checkSelection',
          data: {
            pos,
            isSelected,
            inputDisplay: inputEl.style.display,
            renderDisplay: renderEl.style.display,
          },
          timestamp: Date.now(),
          runId: 'run1',
          hypothesisId: 'B',
        }),
      }).catch(() => {});
      // #endregion
      if (isSelected) {
        if (isHidden(inputContainer) || isHidden(inputEl)) {
          // #region agent log
          fetch('http://127.0.0.1:7242/ingest/1182fcbd-95f8-4860-ba2d-64c3e49fe5b9', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              location: 'mermaid-block.ts:132',
              message: 'Node selected, showing edit mode',
              data: {},
              timestamp: Date.now(),
              runId: 'run1',
              hypothesisId: 'B',
            }),
          }).catch(() => {});
          // #endregion
          showEditMode();
        }
      } else {
        if (isVisible(inputContainer) && isVisible(inputEl)) {
          // #region agent log
          fetch('http://127.0.0.1:7242/ingest/1182fcbd-95f8-4860-ba2d-64c3e49fe5b9', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              location: 'mermaid-block.ts:137',
              message: 'Node not selected, showing render mode',
              data: {},
              timestamp: Date.now(),
              runId: 'run1',
              hypothesisId: 'B',
            }),
          }).catch(() => {});
          // #endregion
          showRenderMode();
        }
      }
    }

    wrapper.addEventListener('click', (e) => {
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/1182fcbd-95f8-4860-ba2d-64c3e49fe5b9', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          location: 'mermaid-block.ts:159',
          message: 'Wrapper click',
          data: {
            inputDisplay: inputEl.style.display,
            renderDisplay: renderEl.style.display,
            targetTag: (e.target as HTMLElement)?.tagName,
            targetIsInput: e.target === inputEl,
          },
          timestamp: Date.now(),
          runId: 'run1',
          hypothesisId: 'E',
        }),
      }).catch(() => {});
      // #endregion
      // Don't handle clicks if clicking on the textarea itself or the info button
      const target = e.target;
      if (
        target === inputEl ||
        target === infoButton ||
        (target instanceof HTMLElement && (inputEl.contains(target) || infoButton.contains(target)))
      ) {
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/1182fcbd-95f8-4860-ba2d-64c3e49fe5b9', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            location: 'mermaid-block.ts:163',
            message: 'Click on textarea, ignoring wrapper handler',
            data: {},
            timestamp: Date.now(),
            runId: 'run1',
            hypothesisId: 'E',
          }),
        }).catch(() => {});
        // #endregion
        return;
      }
      if (isHidden(inputContainer) || isHidden(inputEl)) {
        e.preventDefault();
        e.stopPropagation();
        const pos = getPos();
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/1182fcbd-95f8-4860-ba2d-64c3e49fe5b9', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            location: 'mermaid-block.ts:170',
            message: 'Entering edit mode from click',
            data: { pos },
            timestamp: Date.now(),
            runId: 'run1',
            hypothesisId: 'E',
          }),
        }).catch(() => {});
        // #endregion
        if (typeof pos === 'number') {
          const { state, view } = editor as any;
          const tr: Transaction = state.tr;
          const nodeSelection = NodeSelection.create(tr.doc, pos);
          view.dispatch(tr.setSelection(nodeSelection));
          showEditMode();
        }
      }
    });

    // Ensure textarea can receive clicks and focus - stop propagation but don't prevent default
    inputEl.addEventListener(
      'mousedown',
      (e) => {
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/1182fcbd-95f8-4860-ba2d-64c3e49fe5b9', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            location: 'mermaid-block.ts:185',
            message: 'Textarea mousedown',
            data: { inputDisplay: inputEl.style.display },
            timestamp: Date.now(),
            runId: 'run1',
            hypothesisId: 'E',
          }),
        }).catch(() => {});
        // #endregion
        e.stopPropagation();
        // Don't preventDefault - we want the textarea to receive focus naturally
      },
      true,
    );

    inputEl.addEventListener(
      'click',
      (e) => {
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/1182fcbd-95f8-4860-ba2d-64c3e49fe5b9', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            location: 'mermaid-block.ts:192',
            message: 'Textarea click',
            data: { inputDisplay: inputEl.style.display },
            timestamp: Date.now(),
            runId: 'run1',
            hypothesisId: 'E',
          }),
        }).catch(() => {});
        // #endregion
        e.stopPropagation();
      },
      true,
    );

    inputEl.addEventListener('focus', () => {
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/1182fcbd-95f8-4860-ba2d-64c3e49fe5b9', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          location: 'mermaid-block.ts:200',
          message: 'Textarea focused',
          data: {},
          timestamp: Date.now(),
          runId: 'run1',
          hypothesisId: 'E',
        }),
      }).catch(() => {});
      // #endregion
    });

    renderPromise = renderMermaid(currentNode.attrs.code ?? '');
    // Ensure initial state: render visible, input hidden
    inputEl.style.display = 'none';
    inputContainer.style.display = 'none';
    renderEl.style.display = 'block';

    inputEl.addEventListener('blur', () => {
      setTimeout(() => {
        if (!isThisNodeSelected()) {
          showRenderMode();
        }
      }, 100);
    });

    // Stop event propagation to prevent ProseMirror from intercepting input events
    const stopProp = (e: Event) => e.stopPropagation();
    inputEl.addEventListener('keydown', stopProp, true);
    inputEl.addEventListener('keypress', stopProp, true);
    inputEl.addEventListener('input', stopProp, true);
    inputEl.addEventListener('paste', stopProp, true);
    inputEl.addEventListener('cut', stopProp, true);

    // Handle Escape key separately (needs to run before stopProp)
    inputEl.addEventListener(
      'keydown',
      (e) => {
        if (e.key === 'Escape') {
          inputEl.value = currentNode.attrs.code ?? '';
          if (editor.view?.dom) {
            editor.view.dom.focus();
          }
          showRenderMode();
          e.preventDefault();
          e.stopPropagation();
        }
      },
      true,
    );

    // Update node code as user types (debounced to avoid too many updates)
    let updateTimeout: ReturnType<typeof setTimeout> | null = null;
    inputEl.addEventListener('input', (e) => {
      // #region agent log
      fetch('http://127.0.0.1:7242/ingest/1182fcbd-95f8-4860-ba2d-64c3e49fe5b9', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          location: 'mermaid-block.ts:input',
          message: 'Input event fired',
          data: { newValue: inputEl.value, currentCode: currentNode.attrs.code, pos: getPos() },
          timestamp: Date.now(),
          runId: 'run1',
          hypothesisId: 'D',
        }),
      }).catch(() => {});
      // #endregion
      const pos = getPos();
      if (typeof pos !== 'number') return;
      const newCode = inputEl.value;
      // Clear previous timeout
      if (updateTimeout) {
        clearTimeout(updateTimeout);
      }
      // Debounce updates to avoid excessive command calls
      updateTimeout = setTimeout(() => {
        // #region agent log
        fetch('http://127.0.0.1:7242/ingest/1182fcbd-95f8-4860-ba2d-64c3e49fe5b9', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            location: 'mermaid-block.ts:updateTimeout',
            message: 'Updating mermaid code',
            data: { newCode, oldCode: currentNode.attrs.code, pos },
            timestamp: Date.now(),
            runId: 'run1',
            hypothesisId: 'D',
          }),
        }).catch(() => {});
        // #endregion
        if (newCode !== currentNode.attrs.code) {
          const result = (editor.commands as any).updateMermaid({ code: newCode, pos });
          // #region agent log
          fetch('http://127.0.0.1:7242/ingest/1182fcbd-95f8-4860-ba2d-64c3e49fe5b9', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              location: 'mermaid-block.ts:updateMermaid',
              message: 'updateMermaid result',
              data: { result, pos },
              timestamp: Date.now(),
              runId: 'run1',
              hypothesisId: 'D',
            }),
          }).catch(() => {});
          // #endregion
        }
      }, 300);
    });

    const handleUpdate = () => checkSelection();
    let cleanup: (() => void) | null = null;
    const tiptapEditor = (editor as any).tiptapEditor || (editor as any).editor;
    if (tiptapEditor && typeof tiptapEditor.on === 'function') {
      tiptapEditor.on('selectionUpdate', handleUpdate);
      cleanup = () => {
        if (tiptapEditor && typeof tiptapEditor.off === 'function') {
          tiptapEditor.off('selectionUpdate', handleUpdate);
        }
      };
    }

    // Defer DOM focus listener until view is mounted (node view can run before editor.view is available)
    let editorDomRef: HTMLElement | null = null;
    const attachFocusListener = () => {
      const dom = (editor as any).view?.dom;
      if (!dom || editorDomRef === dom) return;
      editorDomRef = dom;
      const handleFocus = () => checkSelection();
      dom.addEventListener('focus', handleFocus);
      const prevCleanup = cleanup;
      cleanup = () => {
        prevCleanup?.();
        if (dom) {
          dom.removeEventListener('focus', handleFocus);
        }
        editorDomRef = null;
      };
    };
    setTimeout(attachFocusListener, 0);

    setTimeout(() => checkSelection(), 0);

    return {
      dom: wrapper,
      update(updatedNode: PMNode) {
        if (updatedNode.type.name !== nodeName) return false;
        currentNode = updatedNode;
        if (isVisible(inputContainer) && isVisible(inputEl)) {
          inputEl.value = currentNode.attrs.code ?? '';
        } else {
          renderPromise = renderMermaid(currentNode.attrs.code ?? '');
        }
        checkSelection();
        return true;
      },
      destroy() {
        closeSyntaxModal();
        if (cleanup) cleanup();
      },
    };
  };
}

const DEFAULT_MERMAID_CODE = `flowchart LR
  A --> B`;

export const MermaidDiagram = Node.create({
  name: 'mermaidDiagram',

  group: 'block',

  atom: true,

  addAttributes() {
    return {
      code: {
        default: '',
        parseHTML: (element) => element.getAttribute('data-code') ?? '',
        renderHTML: (attributes) => ({
          'data-code': attributes.code,
        }),
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="mermaid"]',
      },
    ];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'mermaid',
        'data-code': node.attrs.code ?? '',
      }),
    ];
  },

  addCommands() {
    return {
      insertMermaid:
        (options) =>
        ({ commands, editor }) => {
          const { code, pos } = options;
          return commands.insertContentAt(pos ?? editor.state.selection.from, {
            type: this.name,
            attrs: { code: code ?? DEFAULT_MERMAID_CODE },
          });
        },
      updateMermaid:
        (options) =>
        ({ editor, tr }) => {
          const { code, pos } = options;
          let resolvedPos = pos;
          if (resolvedPos === undefined) {
            resolvedPos = editor.state.selection.$from.pos;
          }
          const node = editor.state.doc.nodeAt(resolvedPos);
          if (!node || node.type.name !== this.name) {
            return false;
          }
          tr.setNodeMarkup(resolvedPos, this.type, {
            ...node.attrs,
            code: code ?? node.attrs.code,
          });
          return true;
        },
      deleteMermaid:
        (options) =>
        ({ editor, tr }) => {
          const pos = options?.pos ?? editor.state.selection.$from.pos;
          const node = editor.state.doc.nodeAt(pos);
          if (!node || node.type.name !== this.name) {
            return false;
          }
          tr.delete(pos, pos + node.nodeSize);
          return true;
        },
    };
  },

  addNodeView() {
    const editor = this.editor;
    return ({ node, getPos }: { node: PMNode; getPos: () => number | undefined }) =>
      createMermaidNodeView(editor as any, getPos, 'mermaidDiagram')({ node });
  },
});

export default MermaidDiagram;
